<script lang="ts">
  // Wirt fuer einen Seiten-Tab (docs/shell.md).
  //
  // Macht aus einem Pfad eine Flaeche: Route aufloesen, Chunk laden,
  // Seite rendern. Genau die Aufgabe, die frueher in App.svelte sass --
  // nur eben je Tab statt einmal global, denn in der Shell koennen
  // mehrere Seiten gleichzeitig leben.
  //
  // Die Seite bekommt ihre Identitaet ueber den Route-Kontext
  // (page-route.ts), NICHT ueber den globalen Router: sonst zeigen zwei
  // geteilte Host-Seiten denselben Host.

  import type { Component } from "svelte";

  import AnordnungsWirt from "../AnordnungsWirt.svelte";
  import { t } from "../i18n.svelte";
  import { reloadForBrokenChunk } from "../pwa.svelte";
  import { ladeNeu } from "../neuladen";
  import { findRoute } from "../routes";
  import { setPageRoute } from "./page-route";
  import { meldeTabAufgebaut } from "./tab-timing.svelte";

  interface Props {
    path: string;
  }
  let { path }: Props = $props();

  const matched = $derived(findRoute(path));

  // Der Kontext ist eine FUNKTION, kein Wert: der Tab-Inhalt wird nicht
  // neu gemountet, wenn sich nur der Pfad aendert (z.B. ein Query-Teil),
  // und die Seite soll den frischen Wert sehen.
  setPageRoute(() => ({
    path,
    params: matched?.params ?? {},
  }));

  let Page = $state<Component | null>(null);
  let ladefehler = $state(false);

  $effect(() => {
    const r = matched?.route;
    if (!r) {
      Page = null;
      return;
    }
    let abgebrochen = false;
    ladefehler = false;
    r.component()
      .then((m) => {
        if (!abgebrochen) Page = m.default;
      })
      .catch((err) => {
        if (abgebrochen) return;
        // Chunk nicht ladbar -- in aller Regel ein Deploy waehrend
        // offener Sitzung (neue Hashes, alte Datei weg). Die App holt
        // sich per Reload die neue Huelle; greift der Loop-Schutz,
        // sagen wir es ehrlich statt eine leere Flaeche zu zeigen.
        Page = null;
        if (!reloadForBrokenChunk("tab: " + String(err))) ladefehler = true;
      });
    return () => {
      abgebrochen = true;
    };
  });

  // Erstes Bild des Tabs: die Seite hinter dem Lazy-Chunk ist gemountet.
  // Erst ab hier ist der Aufbau des Tabs wirklich vorbei -- die Meldung
  // gehoert deshalb hierher und nicht in den Wirt darueber.
  $effect(() => {
    if (Page) meldeTabAufgebaut();
  });
</script>

{#if matched && Page}
  {@const Seite = Page}
  <!-- Karten der Seite anordnen (lib/anordnung.svelte.ts): je Route EINE
       Reihenfolge, auch fuer Seiten mit Parametern (alle Host-Seiten). -->
  <AnordnungsWirt seite={matched.route.path}>
    <Seite />
  </AnordnungsWirt>
{:else if !matched}
  <div class="grid min-h-[40vh] place-items-center">
    <div class="text-fg-muted t-body">404 — {path}</div>
  </div>
{:else if ladefehler}
  <div class="grid min-h-[40vh] place-items-center">
    <div class="surface border-border rounded-xl border p-8 text-center">
      <p class="t-headline text-fg">
        {t("app.chunk_failed.title", "Seite konnte nicht geladen werden")}
      </p>
      <p class="t-meta text-fg-muted mt-2 max-w-sm">
        {t(
          "app.chunk_failed.body",
          "Meist liegt das an einem Update, das gerade ausgerollt wurde. Neu laden holt die aktuelle Version.",
        )}
      </p>
      <button
        type="button"
        onclick={() => ladeNeu("knopf")}
        class="border-accent/50 bg-accent text-accent-fg t-body mt-4 rounded-lg border px-4 py-2 font-medium transition hover:opacity-90"
      >
        {t("app.chunk_failed.reload", "Neu laden")}
      </button>
    </div>
  </div>
{/if}
